import { useT, type Translate } from "../../../../shared/i18n/i18n";
import { DndSortableVerticalList, useDndSortableItem } from "../../../../shared/ui/multi-container-dnd";
import { useQueryClient } from "@tanstack/react-query";
import { FormEvent, type ReactNode, useMemo, useState } from "react";
import type {
  CreateWorkObjectFieldInput,
  EditableWorkObjectFieldType,
  WorkObjectFieldOptionInput,
  WorkObjectFieldLocationInput,
  WorkObjectFieldDefinition,
} from "../../../../entities/work-object-field/model/use-work-object-fields";
import { slugifyWorkObjectFieldKey } from "../../../../entities/work-object-field/model/work-object-field-key";
import {
  createFieldFormState,
  editableFieldTypes,
  fieldConfigForSubmit,
  getKeyError,
  getNameError,
  isConfigurableLocationScope,
  isItemRelationFieldType,
  isSelectFieldType,
  locationKey,
  readProgressFieldConfig,
  type FieldKeyError,
  type FieldNameError,
  type RatingScale,
  type RelationConfig,
} from "../model/field-form-state";
import { getWorkObjectFieldTypeMeta } from "../../../../entities/work-object-field/model/work-object-field-type-meta";
import type { WorkObjectFieldAuditEvent } from "../../../../entities/work-object-field/model/use-work-object-field-audit";
import { WorkObjectFieldAuditList } from "../../../../entities/work-object-field/ui/work-object-field-audit-list";
import type { WorkObjectTypeDefinition } from "../../../../entities/work-object-type/model/use-work-object-types";
import { WorkObjectTypeIconGlyph } from "../../../../entities/work-object-type/ui/work-object-type-icon";
import { FormulaExpressionEditor } from "./formula-expression-editor";
import { EditableText } from "@/shared/ui/editable-text";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useWorkspaceTeam } from "../../../../entities/access/model/use-workspace-access";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/shared/api/trpc";
import type { LucideIcon } from "lucide-react";
import {
  Archive,
  ChevronDown,
  Database,
  Folder,
  Globe2,
  GripVertical,
  History,
  LayoutDashboard,
  Lock,
  Plus,
  Trash2,
  X,
} from "lucide-react";

export type SaveWorkObjectFieldInput = CreateWorkObjectFieldInput & {
  applicableTypeIds: string[];
  appliesToAllTypes: boolean;
  locations: WorkObjectFieldLocationInput[];
  // Work item types this relation may point at. Empty = any type.
  relationTargetTypeIds: string[];
};

type WorkObjectFieldDialogProps = {
  field: WorkObjectFieldDefinition | null;
  auditEvents: WorkObjectFieldAuditEvent[];
  availableBoards: Array<{ folderId: string | null; id: string; name: string; spaceId: string }>;
  availableFolders: Array<{ id: string; name: string; parentFolderId: string | null; spaceId: string }>;
  availableSpaces: Array<{ id: string; name: string }>;
  availableTypes: WorkObjectTypeDefinition[];
  existingFields: WorkObjectFieldDefinition[];
  isArchiving: boolean;
  isLoadingAudit: boolean;
  isSaving: boolean;
  onArchive: (fieldId: string) => Promise<void>;
  onArchiveOption: (input: {
    fieldId: string;
    optionId: string;
    replacementOptionId?: string | null;
  }) => Promise<WorkObjectFieldDefinition>;
  onOpenChange: (open: boolean) => void;
  onSave: (input: SaveWorkObjectFieldInput, field: WorkObjectFieldDefinition | null) => Promise<void>;
  open: boolean;
  workspaceId: string | null;
};

type LocationTreeNode = {
  children: LocationTreeNode[];
  icon: LucideIcon;
  key: string;
  label: string;
  scopeType: WorkObjectFieldLocationInput["scopeType"];
};

export function WorkObjectFieldDialog({
  field,
  auditEvents,
  availableBoards,
  availableFolders,
  availableSpaces,
  availableTypes,
  existingFields,
  isArchiving,
  isLoadingAudit,
  isSaving,
  onArchive,
  onArchiveOption,
  onOpenChange,
  onSave,
  open,
  workspaceId,
}: WorkObjectFieldDialogProps) {
  const t = useT();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [isKeyDirty, setIsKeyDirty] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [type, setType] = useState<EditableWorkObjectFieldType>("text");
  const [fieldOptions, setFieldOptions] = useState<WorkObjectFieldOptionInput[]>([]);
  const [ratingScale, setRatingScale] = useState<RatingScale>(5);
  const [formulaExpression, setFormulaExpression] = useState("");
  const [rollupRelationFieldId, setRollupRelationFieldId] = useState("");
  const [rollupSourceFieldId, setRollupSourceFieldId] = useState("");
  const [rollupOperator, setRollupOperator] = useState("count");
  const [rollupStatusCategories, setRollupStatusCategories] = useState<string[]>([]);
  const [relationInverseName, setRelationInverseName] = useState("");
  const [relationSingleSource, setRelationSingleSource] = useState(false);
  const [relationIsHierarchy, setRelationIsHierarchy] = useState(false);
  const [relationTargetTypeIds, setRelationTargetTypeIds] = useState<string[]>([]);
  const [isProgressEnabled, setIsProgressEnabled] = useState(false);
  const [progressMax, setProgressMax] = useState("100");
  // Границы числа. Пусто значит «без правила» — поле ведёт себя как раньше.
  const [numberMin, setNumberMin] = useState("");
  const [numberMax, setNumberMax] = useState("");
  const [numberStep, setNumberStep] = useState("");
  const [personScopeTeamId, setPersonScopeTeamId] = useState("");
  // Команды рабочей области: из них выбирается роль person-поля. Экран полей и
  // так открыт менеджеру, поэтому отдельного доступа не нужно.
  const workspaceTeam = useWorkspaceTeam();
  const roleOptions = [
    { label: t("fieldDialog.personRoleAnyone"), value: "" },
    ...(workspaceTeam.data?.teams ?? []).map((team) => ({ label: team.name, value: team.id })),
  ];
  const [isFormulaDirty, setIsFormulaDirty] = useState(false);
  const [locationKeys, setLocationKeys] = useState<string[]>([]);
  const [applicableTypeIds, setApplicableTypeIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [formulaError, setFormulaError] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const effectiveWorkspaceId = field?.workspaceId ?? workspaceId ?? "";
  const isExisting = field !== null;
  // System fields (Title, Type, board statuses) are wired into the core data
  // model — the dialog only shows them, nothing is editable.
  const isReadOnly = field?.origin === "system";
  const canRename = (!isExisting || field.canRename) && !isReadOnly;
  const canArchive = Boolean(field?.canDelete) && !isReadOnly;
  const selectedLocationCount = locationKeys.length;
  const appliesToAllTypes = applicableTypeIds.length === 0;
  const selectedTypeCount = applicableTypeIds.length;
  const title = isExisting ? t("fieldDialog.settings") : t("fieldDialog.newField");
  const normalizedKey = slugifyWorkObjectFieldKey(key);
  const nameValidationError = getNameError(name);
  const keyValidationError = getKeyError(normalizedKey, existingFields, field?.id ?? null);
  const nameError = isSubmitAttempted ? fieldNameErrorText(nameValidationError, t) : null;
  const keyError = isSubmitAttempted ? fieldKeyErrorText(keyValidationError, t) : null;
  const fieldTypeMeta = getWorkObjectFieldTypeMeta(type, t);
  const FieldTypeIcon = fieldTypeMeta.icon;

  const locationTree = useMemo(() => {
    const folderChildren = new Map<string | null, LocationTreeNode[]>();
    const boardChildren = new Map<string | null, LocationTreeNode[]>();

    for (const folder of availableFolders) {
      const parentKey = folder.parentFolderId ? `folder:${folder.parentFolderId}` : `space:${folder.spaceId}`;
      const children = folderChildren.get(parentKey) ?? [];
      children.push({
        children: [],
        icon: Folder,
        key: locationKey("folder", folder.id),
        label: folder.name,
        scopeType: "folder",
      });
      folderChildren.set(parentKey, children);
    }

    for (const board of availableBoards) {
      const parentKey = board.folderId ? `folder:${board.folderId}` : `space:${board.spaceId}`;
      const children = boardChildren.get(parentKey) ?? [];
      children.push({
        children: [],
        icon: LayoutDashboard,
        key: locationKey("board", board.id),
        label: board.name,
        scopeType: "board",
      });
      boardChildren.set(parentKey, children);
    }

    function fillChildren(node: LocationTreeNode) {
      const folderNodes = folderChildren.get(node.key) ?? [];
      const boardNodes = boardChildren.get(node.key) ?? [];
      node.children = [...folderNodes, ...boardNodes];
      for (const child of folderNodes) {
        fillChildren(child);
      }
      return node;
    }

    const spaceNodes = availableSpaces.map((space) =>
      fillChildren({
        children: [],
        icon: Database,
        key: locationKey("space", space.id),
        label: space.name,
        scopeType: "space",
      }),
    );

    if (!effectiveWorkspaceId) {
      return spaceNodes;
    }

    return [
      {
        children: spaceNodes,
        icon: Globe2,
        key: locationKey("workspace", effectiveWorkspaceId),
        label: t("fieldDialog.workspace"),
        scopeType: "workspace" as const,
      },
    ];
  }, [availableBoards, availableFolders, availableSpaces, effectiveWorkspaceId]);

  // Состояние формы выставляется на рендере, а не эффектом. Эффект выполняется
  // уже после того, как содержимое диалога отрисовалось и получило автофокус, —
  // а сфокусированный EditableText переходит в режим правки и больше не
  // принимает внешнее значение. Именно так существующее поле открывалось с
  // пустыми Field name и Field key.
  const seedToken = open ? `${field?.id ?? "new"}:${field?.workspaceId ?? workspaceId ?? ""}` : "";
  const [seededToken, setSeededToken] = useState<string | null>(null);
  if (seedToken !== seededToken) {
    setSeededToken(seedToken);
    const seed = createFieldFormState(field, workspaceId);
    setName(seed.name);
    setKey(seed.key);
    setIsKeyDirty(seed.isKeyDirty);
    setIsSubmitAttempted(false);
    setType(seed.type);
    setFieldOptions(seed.fieldOptions);
    setRatingScale(seed.ratingScale);
    setRelationInverseName(seed.relationInverseName);
    setRelationSingleSource(seed.relationSingleSource);
    setRelationIsHierarchy(seed.relationIsHierarchy);
    setRelationTargetTypeIds(seed.relationTargetTypeIds);
    setFormulaExpression(seed.formulaExpression);
    setRollupRelationFieldId(seed.rollupRelationFieldId);
    setRollupSourceFieldId(seed.rollupSourceFieldId);
    setRollupOperator(seed.rollupOperator);
    setRollupStatusCategories(seed.rollupStatusCategories);
    setIsProgressEnabled(seed.isProgressEnabled);
    setProgressMax(seed.progressMax);
    setNumberMin(seed.numberMin);
    setNumberMax(seed.numberMax);
    setNumberStep(seed.numberStep);
    setPersonScopeTeamId(seed.personScopeTeamId);
    setIsFormulaDirty(false);
    setLocationKeys(seed.locationKeys);
    setApplicableTypeIds(seed.applicableTypeIds);
    setError(null);
    setFormulaError(null);
    setIsHistoryOpen(false);
  }

  const isFormValid = !nameValidationError && !keyValidationError && locationKeys.length > 0 && Boolean(effectiveWorkspaceId);

  function updateName(nextName: string) {
    setName(nextName);
    if (!isKeyDirty) {
      setKey(slugifyWorkObjectFieldKey(nextName));
    }
  }

  function updateKey(nextKey: string) {
    setIsKeyDirty(true);
    setKey(slugifyWorkObjectFieldKey(nextKey));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (isReadOnly) return;
    setIsSubmitAttempted(true);
    if (!isFormValid) {
      return;
    }

    setError(null);
    setFormulaError(null);
    try {
      await onSave(
        {
          applicableTypeIds,
          appliesToAllTypes,
          locations: locationKeys.map((key) => {
            const [scopeType, scopeId] = key.split(":");
            return {
              isRequired: false,
              scopeId: scopeId ?? "",
              scopeType: scopeType as WorkObjectFieldLocationInput["scopeType"],
            };
          }),
          key: normalizedKey,
          name: name.trim(),
          config: fieldConfigForSubmit({
            formulaExpression,
            numberRange: numberRangeInput(numberMin, numberMax, numberStep),
            personScopeTeamId,
            progress: isProgressEnabled ? { max: Number(progressMax) > 0 ? Number(progressMax) : 100 } : null,
            ratingScale,
            relation: {
              inverseCardinality: relationSingleSource ? "one" : "many",
              inverseName: relationInverseName.trim(),
              isHierarchy: relationIsHierarchy,
            },
            rollupOperator,
            rollupRelationFieldId,
            rollupSourceFieldId,
            rollupStatusCategories,
            type,
          }),
          options: isSelectFieldType(type) ? fieldOptions : [],
          relationTargetTypeIds: isItemRelationFieldType(type) ? relationTargetTypeIds : [],
          type,
        },
        field,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : t("fieldDialog.couldNotSave");
      if (type === "formula") {
        setFormulaError(message);
      } else {
        setError(message);
      }
    }
  }

  async function archive() {
    if (!field) {
      return;
    }

    setError(null);
    try {
      await onArchive(field.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("fieldDialog.couldNotArchive"));
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={cn(
          "flex max-h-[min(760px,calc(100dvh-2rem))] flex-col gap-0 overflow-hidden p-0 transition-[max-width]",
          isHistoryOpen ? "sm:!max-w-5xl" : "sm:!max-w-3xl",
        )}
        showCloseButton={false}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t("fieldDialog.describe")}</DialogDescription>
        </DialogHeader>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
            <div className="relative min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-8 pt-8 pb-5">
              <DialogClose
                render={
                  <Button
                    aria-label={t("fieldDialog.closeSettings")}
                    className="absolute top-2 right-2"
                    size="icon-sm"
                    type="button"
                    variant="ghost"
                  />
                }
              >
                <X />
                <span className="sr-only">{t("fieldDialog.close")}</span>
              </DialogClose>

              <div className="grid gap-6">
                <div className="flex items-start gap-4">
                  <div className="grid size-11 shrink-0 place-items-center rounded-lg border border-border/70 bg-muted/20 text-muted-foreground">
                    <FieldTypeIcon className="size-5" />
                  </div>
                  <div className="grid min-w-0 flex-1 gap-1 pt-0.5">
                    {canRename ? (
                      <EditableText
                        ariaLabel={t("fieldDialog.fieldName")}
                        className="min-w-0"
                        inputClassName="text-base font-semibold leading-6"
                        onChange={updateName}
                        onInputChange={updateName}
                        placeholder={t("fieldDialog.untitled")}
                        previewClassName="text-base font-semibold leading-6"
                        selectOnFocus={!isExisting}
                        value={name}
                      />
                    ) : (
                      <p className="truncate text-base font-semibold leading-6">{name}</p>
                    )}
                    {nameError ? <FieldError>{nameError}</FieldError> : null}
                    <div className="min-w-0 text-xs text-muted-foreground">
                      {canRename ? (
                        <EditableText
                          ariaLabel={t("fieldDialog.fieldKey")}
                          className="min-w-0"
                          inputClassName="font-mono text-xs leading-5 text-muted-foreground"
                          onChange={updateKey}
                          onInputChange={updateKey}
                          placeholder=""
                          previewClassName="font-mono text-xs leading-5 text-muted-foreground"
                          value={key}
                        />
                      ) : (
                        <span className="block min-w-0 truncate font-mono leading-5">{field?.key ?? key}</span>
                      )}
                    </div>
                    {keyError ? <FieldError>{keyError}</FieldError> : null}
                  </div>
                </div>

                <div className="grid gap-4">
                  <FieldSelect
                    disabled={isExisting || isSaving}
                    displayValue={fieldTypeMeta.label}
                    icon={fieldTypeMeta.icon}
                    label={t("fieldType.type")}
                    onValueChange={(value) => setType(value as EditableWorkObjectFieldType)}
                    value={type}
                  >
                    {editableFieldTypes.map((optionType) => (
                      <SelectItem className="items-start gap-2.5 py-2 pr-8" key={optionType} value={optionType}>
                        <FieldTypeOption type={optionType} />
                      </SelectItem>
                    ))}
                  </FieldSelect>

                  {isSelectFieldType(type) ? (
                    <FieldOptionsEditor
                      disabled={isSaving || isReadOnly}
                      fieldId={field?.id}
                      onArchiveOption={async (optionId, replacementOptionId) => {
                        if (!field) {
                          return;
                        }
                        const updated = await onArchiveOption({ fieldId: field.id, optionId, replacementOptionId });
                        setFieldOptions(
                          updated.options.map((option) => ({
                            color: option.color,
                            id: option.id,
                            rank: option.rank,
                            value: option.value,
                          })),
                        );
                      }}
                      onOptionsChange={setFieldOptions}
                      options={fieldOptions}
                    />
                  ) : null}

                  {isItemRelationFieldType(type) ? (
                    <div className="flex flex-col gap-3">
                      <Field>
                        <FieldLabel htmlFor="relation-inverse-name">{t("fieldDialog.nameFromOtherSide")}</FieldLabel>
                        <Input
                          autoComplete="off"
                          disabled={isSaving || isReadOnly}
                          id="relation-inverse-name"
                          maxLength={80}
                          onChange={(event) => setRelationInverseName(event.target.value)}
                          placeholder={t("fieldDialog.inversePlaceholder")}
                          value={relationInverseName}
                        />
                        <FieldDescription>
                          {t("fieldDialog.inverseFallback", { name: name.trim() || t("fieldDialog.fallbackFieldName") })}
                        </FieldDescription>
                      </Field>
                      <Field>
                        <FieldLabel>{t("fieldDialog.canPointAt")}</FieldLabel>
                        <WorkObjectTypeMultiSelect
                          applicableTypeIds={relationTargetTypeIds}
                          availableTypes={availableTypes}
                          disabled={isSaving || isReadOnly}
                          onApplicableTypeIdsChange={setRelationTargetTypeIds}
                        />
                        <FieldDescription>{t("fieldDialog.leaveEmptyAnyType")}</FieldDescription>
                      </Field>
                      <Field>
                        <FieldLabel className="flex items-center gap-2">
                          <Checkbox
                            checked={relationSingleSource}
                            disabled={isSaving || isReadOnly}
                            onCheckedChange={(checked) => setRelationSingleSource(checked === true)}
                          />
                          {t("fieldDialog.onlyOnce")}
                        </FieldLabel>
                        <FieldDescription>{t("fieldDialog.twoCannotLink")}</FieldDescription>
                      </Field>
                      <Field>
                        <FieldLabel className="flex items-center gap-2">
                          <Checkbox
                            checked={relationIsHierarchy}
                            disabled={isSaving || isReadOnly}
                            onCheckedChange={(checked) => setRelationIsHierarchy(checked === true)}
                          />
                          {t("fieldDialog.thisIsHierarchy")}
                        </FieldLabel>
                        <FieldDescription>{t("fieldDialog.acyclic")}</FieldDescription>
                      </Field>
                    </div>
                  ) : null}

                  {type === "rating" ? (
                    <FieldSelect
                      disabled={isExisting || isSaving}
                      displayValue={`${ratingScale} point scale`}
                      label={t("fieldDialog.ratingScale")}
                      onValueChange={(value) => setRatingScale(Number(value) as RatingScale)}
                      value={String(ratingScale)}
                    >
                      <SelectItem value="5">5 point scale</SelectItem>
                      <SelectItem value="10">10 point scale</SelectItem>
                      <SelectItem value="100">100 point scale</SelectItem>
                    </FieldSelect>
                  ) : null}

                  {type === "formula" ? (
                    <FormulaExpressionEditor
                      currentFieldId={field?.id ?? null}
                      disabled={isSaving}
                      externalError={formulaError}
                      fields={existingFields}
                      onChange={(nextExpression) => {
                        setFormulaError(null);
                        setIsFormulaDirty(true);
                        setFormulaExpression(nextExpression);
                      }}
                      onPreview={(expression) =>
                        queryClient.fetchQuery(trpc.workObjectFields.previewFormula.queryOptions({
                          currentFieldId: field?.id ?? null,
                          expression,
                        }))
                      }
                      showValidationMessages={isSubmitAttempted || isFormulaDirty}
                      value={formulaExpression}
                    />
                  ) : null}

                  {["formula", "number", "percent", "rollup"].includes(type) ? (
                    <Field>
                      <FieldLabel className="flex items-center gap-2">
                        <Checkbox
                          checked={isProgressEnabled}
                          disabled={isSaving}
                          onCheckedChange={(checked) => setIsProgressEnabled(checked === true)}
                        />
                        {t("fieldDialog.showAsProgress")}
                      </FieldLabel>
                      {isProgressEnabled ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">100% at</span>
                          <Input
                            className="h-8 w-28"
                            disabled={isSaving}
                            inputMode="decimal"
                            onChange={(event) => setProgressMax(event.target.value)}
                            type="number"
                            value={progressMax}
                          />
                        </div>
                      ) : null}
                    </Field>
                  ) : null}

                  {["user_multi_relation", "user_relation"].includes(type) ? (
                    <Field>
                      <FieldLabel>{t("fieldDialog.personRole")}</FieldLabel>
                      <Select
                        items={roleOptions}
                        onValueChange={(next) => setPersonScopeTeamId(next === null ? "" : String(next))}
                        value={personScopeTeamId}
                      >
                        <SelectTrigger aria-label={t("fieldDialog.personRole")} className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {roleOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <FieldDescription>{t("fieldDialog.personRoleHint")}</FieldDescription>
                    </Field>
                  ) : null}

                  {["currency", "number", "percent"].includes(type) ? (
                    <Field>
                      <FieldLabel>{t("fieldDialog.numberRange")}</FieldLabel>
                      <div className="flex flex-wrap items-center gap-2">
                        <NumberRuleInput
                          disabled={isSaving}
                          label={t("fieldDialog.numberMin")}
                          onChange={setNumberMin}
                          value={numberMin}
                        />
                        <NumberRuleInput
                          disabled={isSaving}
                          label={t("fieldDialog.numberMax")}
                          onChange={setNumberMax}
                          value={numberMax}
                        />
                        <NumberRuleInput
                          disabled={isSaving}
                          label={t("fieldDialog.numberStep")}
                          onChange={setNumberStep}
                          value={numberStep}
                        />
                      </div>
                      <FieldDescription>{t("fieldDialog.numberRangeHint")}</FieldDescription>
                    </Field>
                  ) : null}

                  {type === "rollup" ? (
                    <RollupConfigFields
                      disabled={isSaving}
                      fields={existingFields}
                      operator={rollupOperator}
                      relationFieldId={rollupRelationFieldId}
                      sourceFieldId={rollupSourceFieldId}
                      statusCategories={rollupStatusCategories}
                      onOperatorChange={setRollupOperator}
                      onRelationFieldChange={setRollupRelationFieldId}
                      onSourceFieldChange={setRollupSourceFieldId}
                      onStatusCategoriesChange={setRollupStatusCategories}
                    />
                  ) : null}

                  <Field>
                    <FieldLabel>{t("fieldDialog.appliedTo")}</FieldLabel>
                    <WorkObjectTypeMultiSelect
                      applicableTypeIds={applicableTypeIds}
                      availableTypes={availableTypes}
                      disabled={isSaving || isReadOnly}
                      onApplicableTypeIdsChange={setApplicableTypeIds}
                    />
                  </Field>

                  <Field>
                    <FieldLabel className="justify-between">
                      <span>{t("fieldDialog.visibleIn")}</span>
                      <span className="font-normal text-muted-foreground">{selectedLocationCount} selected</span>
                    </FieldLabel>
                    <FieldGroup className={cn("gap-0.5", isReadOnly && "pointer-events-none opacity-60")} data-slot="checkbox-group">
                      <LocationTree
                        checkedKeys={locationKeys}
                        nodes={locationTree}
                        onCheckedChange={(key, checked) =>
                          setLocationKeys((current) => toggleLocationKey(current, locationTree, key, checked))
                        }
                      />
                    </FieldGroup>
                    {isSubmitAttempted && locationKeys.length === 0 ? (
                      <FieldError>{t("fieldDialog.selectVisibility")}</FieldError>
                    ) : null}
                  </Field>
                </div>
              </div>
            </div>

            {isHistoryOpen ? (
              <FieldHistoryPanel
                events={auditEvents}
                isLoading={isLoadingAudit}
                onClose={() => setIsHistoryOpen(false)}
              />
            ) : null}
          </div>

          {error ? (
            <Alert className="mx-5 mb-4" variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="shrink-0 items-center justify-between border-t border-border/70 px-8 py-3 sm:justify-between">
            <div className="flex min-w-0 items-center gap-2">
              {isExisting ? (
                <Button
                  aria-pressed={isHistoryOpen}
                  onClick={() => setIsHistoryOpen((current) => !current)}
                  type="button"
                  variant={isHistoryOpen ? "secondary" : "ghost"}
                >
                  <History />
                  {t("fieldDialog.historyShort")}
                </Button>
              ) : null}
              {canArchive ? (
                <AlertDialog>
                  <AlertDialogTrigger
                    render={
                      <Button className="text-destructive hover:text-destructive" disabled={isArchiving || isSaving} type="button" variant="ghost" />
                    }
                  >
                    <Archive />
                    {t("fieldDialog.archive")}
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("fieldDialog.archiveConfirm")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("fieldDialog.archiveHint")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction disabled={isArchiving} onClick={archive} variant="destructive">
                        {isArchiving ? t("fieldDialog.archiving") : t("fieldDialog.archiveField")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                <span className="inline-flex h-8 items-center gap-1.5 text-xs text-muted-foreground">
                  {isExisting ? <Lock className="size-3.5" /> : null}
                  {isExisting ? (isReadOnly ? t("fieldDialog.systemReadOnly") : t("fieldDialog.systemCannotArchive")) : null}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
                {isReadOnly ? t("fieldDialog.close") : t("common.cancel")}
              </Button>
              {isReadOnly ? null : (
                <Button disabled={isSaving || !effectiveWorkspaceId} type="submit">
                  {isSaving ? t("fieldDialog.saving") : isExisting ? t("fieldDialog.saveChanges") : t("common.create")}
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const rollupStatusCategoryOptions: Array<{ labelKey: string; value: string }> = [
  { labelKey: "statuses.notStarted", value: "not_started" },
  { labelKey: "statuses.active", value: "active" },
  { labelKey: "statuses.done", value: "done" },
  { labelKey: "statuses.closed", value: "closed" },
];

function RollupConfigFields({
  disabled,
  fields,
  operator,
  relationFieldId,
  sourceFieldId,
  statusCategories,
  onOperatorChange,
  onRelationFieldChange,
  onSourceFieldChange,
  onStatusCategoriesChange,
}: {
  disabled?: boolean;
  fields: WorkObjectFieldDefinition[];
  operator: string;
  relationFieldId: string;
  sourceFieldId: string;
  statusCategories: string[];
  onOperatorChange: (operator: string) => void;
  onRelationFieldChange: (fieldId: string) => void;
  onSourceFieldChange: (fieldId: string) => void;
  onStatusCategoriesChange: (categories: string[]) => void;
}) {
  const t = useT();
  const relationFields = fields.filter((field) => field.type === "work_object_relation" || field.type === "work_object_multi_relation");
  const sourceFields = fields.filter((field) => field.type !== "rollup" && field.type !== "work_object_relation" && field.type !== "work_object_multi_relation");
  const needsSource = operator !== "count";

  return (
    <div className="grid gap-4">
      <FieldSelect
        disabled={disabled || relationFields.length === 0}
        displayValue={relationFields.find((field) => field.id === relationFieldId)?.name ?? t("fieldDialog.chooseRelation")}
        label={t("fieldDialog.relation")}
        onValueChange={onRelationFieldChange}
        value={relationFieldId}
      >
        {relationFields.map((field) => (
          <SelectItem key={field.id} value={field.id}>
            {field.name}
          </SelectItem>
        ))}
      </FieldSelect>

      <FieldSelect
        disabled={disabled}
        displayValue={rollupOperatorLabel(operator, t)}
        label={t("fieldDialog.calculate")}
        onValueChange={onOperatorChange}
        value={operator}
      >
        {["count", "sum", "avg", "min", "max", "filled", "empty"].map((option) => (
          <SelectItem key={option} value={option}>
            {rollupOperatorLabel(option, t)}
          </SelectItem>
        ))}
      </FieldSelect>

      {needsSource ? (
        <FieldSelect
          disabled={disabled || sourceFields.length === 0}
          displayValue={sourceFields.find((field) => field.id === sourceFieldId)?.name ?? t("fieldDialog.chooseField")}
          label={t("fieldDialog.sourceField")}
          onValueChange={onSourceFieldChange}
          value={sourceFieldId}
        >
          {sourceFields.map((field) => (
            <SelectItem key={field.id} value={field.id}>
              {field.name}
            </SelectItem>
          ))}
        </FieldSelect>
      ) : null}

      <Field>
        <FieldLabel>{t("fieldDialog.countStatuses")}</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {rollupStatusCategoryOptions.map((option) => {
            const isOn = statusCategories.includes(option.value);
            return (
              <button
                aria-pressed={isOn}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs transition-colors",
                  isOn ? "border-primary/60 bg-primary/10" : "border-border/60 hover:bg-accent/40",
                )}
                disabled={disabled}
                key={option.value}
                onClick={() =>
                  onStatusCategoriesChange(
                    isOn ? statusCategories.filter((entry) => entry !== option.value) : [...statusCategories, option.value],
                  )
                }
                type="button"
              >
                {t(option.labelKey)}
              </button>
            );
          })}
        </div>
        <FieldDescription>
          {statusCategories.length === 0
            ? t("fieldDialog.rollupAnyStatus")
            : t("fieldDialog.rollupOnlyStatuses")}
        </FieldDescription>
      </Field>
    </div>
  );
}

function WorkObjectTypeMultiSelect({
  applicableTypeIds,
  availableTypes,
  disabled,
  onApplicableTypeIdsChange,
}: {
  applicableTypeIds: string[];
  availableTypes: WorkObjectTypeDefinition[];
  disabled?: boolean;
  onApplicableTypeIdsChange: (ids: string[]) => void;
}) {
  const t = useT();
  const appliesToAllTypes = applicableTypeIds.length === 0;
  const selectedTypes = availableTypes.filter((workObjectType) => applicableTypeIds.includes(workObjectType.id));
  const selectedType = selectedTypes.length === 1 ? selectedTypes[0] : null;
  const summary = appliesToAllTypes
    ? t("fieldDialog.allItemTypes")
    : t("fieldDialog.typesSelected", { count: selectedTypes.length });
  const visibleSelectedTypes = selectedTypes.slice(0, 4);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            aria-label={t("fieldDialog.chooseTypes")}
            className="w-full justify-between border-input bg-input/20 font-normal text-foreground hover:bg-input/30 dark:bg-input/30"
            disabled={disabled}
            type="button"
            variant="outline"
          />
        }
      >
        <span className="flex min-w-0 items-center gap-2 truncate text-left">
          {selectedType ? <WorkObjectTypeIconGlyph className="size-3.5 text-muted-foreground" icon={selectedType.icon} /> : null}
          {!appliesToAllTypes && selectedTypes.length > 1 ? (
            <span className="flex shrink-0 items-center -space-x-1">
              {visibleSelectedTypes.map((workObjectType) => (
                <span className="grid size-4 place-items-center text-muted-foreground" key={workObjectType.id}>
                  <WorkObjectTypeIconGlyph className="size-3" icon={workObjectType.icon} />
                </span>
              ))}
            </span>
          ) : null}
          <span className="min-w-0 truncate">{selectedType?.name ?? summary}</span>
        </span>
        <ChevronDown data-icon="inline-end" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-72">
        <DropdownMenuGroup>
          <DropdownMenuLabel>{t("fieldDialog.types")}</DropdownMenuLabel>
          {availableTypes.length > 0 ? (
            availableTypes.map((workObjectType) => (
              <DropdownMenuCheckboxItem
                checked={applicableTypeIds.includes(workObjectType.id)}
                closeOnClick={false}
                key={workObjectType.id}
                onCheckedChange={(checked) => {
                  onApplicableTypeIdsChange(toggleKey(applicableTypeIds, workObjectType.id, checked === true));
                }}
              >
                <span className="inline-flex min-w-0 items-center gap-2">
                  <WorkObjectTypeIconGlyph className="size-3.5 text-muted-foreground" icon={workObjectType.icon} />
                  <span className="min-w-0 truncate">{workObjectType.name}</span>
                </span>
              </DropdownMenuCheckboxItem>
            ))
          ) : (
            <DropdownMenuItem disabled>{t("fieldDialog.noTypes")}</DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FieldSelect({
  children,
  description,
  disabled,
  displayValue,
  icon: Icon,
  label,
  onValueChange,
  value,
}: {
  children: ReactNode;
  description?: string | null;
  disabled?: boolean;
  displayValue?: ReactNode;
  icon?: LucideIcon;
  label: string;
  onValueChange: (value: string) => void;
  value: string;
}) {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Select
        disabled={disabled}
        onValueChange={(nextValue) => {
          if (nextValue !== null) {
            onValueChange(nextValue);
          }
        }}
        value={value}
      >
        <SelectTrigger aria-label={label} className="w-full">
          {Icon ? <Icon className="size-3.5 text-muted-foreground" /> : null}
          <SelectValue>{displayValue}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>{children}</SelectGroup>
        </SelectContent>
      </Select>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
    </Field>
  );
}

function FieldTypeOption({ type }: { type: EditableWorkObjectFieldType }) {
  const t = useT();
  const meta = getWorkObjectFieldTypeMeta(type, t);
  const Icon = meta.icon;

  return (
    <span className="flex min-w-0 flex-1 items-start gap-2.5">
      <Icon className="mt-[3px] size-3.5 text-muted-foreground" />
      <span className="!grid min-w-0 flex-1 gap-0.5">
        <span className="truncate font-medium text-foreground">{meta.label}</span>
        <span className="whitespace-normal text-xs leading-snug text-muted-foreground">{meta.description}</span>
      </span>
    </span>
  );
}

const optionColors = ["#8b5cf6", "#3b82f6", "#06b6d4", "#22c55e", "#eab308", "#f97316", "#ef4444", "#ec4899"];

function FieldOptionsEditor({
  disabled,
  fieldId,
  onArchiveOption,
  onOptionsChange,
  options,
}: {
  disabled?: boolean;
  fieldId?: string;
  onArchiveOption?: (optionId: string, replacementOptionId: string | null) => Promise<void>;
  onOptionsChange: (options: WorkObjectFieldOptionInput[]) => void;
  options: WorkObjectFieldOptionInput[];
}) {
  const t = useT();
  function updateOption(index: number, patch: Partial<WorkObjectFieldOptionInput>) {
    onOptionsChange(options.map((option, optionIndex) => (optionIndex === index ? { ...option, ...patch } : option)));
  }

  function addOption() {
    onOptionsChange([
      ...options,
      {
        color: optionColors[options.length % optionColors.length] ?? "#8b5cf6",
        rank: nextOptionRank(options.length),
        value: "",
      },
    ]);
  }

  function removeOption(index: number) {
    onOptionsChange(rerankOptions(options.filter((_, optionIndex) => optionIndex !== index)));
  }

  function handleReorder(orderedItemIds: string[]) {
    const optionById = new Map(options.map((option, index) => [fieldOptionDraggableId(option, index), option]));
    const reordered = orderedItemIds
      .map((id) => optionById.get(id))
      .filter((option): option is WorkObjectFieldOptionInput => Boolean(option));
    if (reordered.length !== options.length) {
      return;
    }
    onOptionsChange(rerankOptions(reordered));
  }

  return (
    <Field>
      <FieldLabel className="justify-between">
        <span>{t("fieldDialog.options")}</span>
        <span className="font-normal text-muted-foreground">{options.length}</span>
      </FieldLabel>
      <div className="grid gap-1.5">
        <DndSortableVerticalList
          itemIds={options.map((option, index) => fieldOptionDraggableId(option, index))}
          onReorder={({ orderedItemIds }) => handleReorder(orderedItemIds)}
        >
          <div className="grid gap-1">
            {options.map((option, index) => (
              <FieldOptionRow
                disabled={disabled}
                fieldId={fieldId}
                index={index}
                key={fieldOptionDraggableId(option, index)}
                onArchiveOption={onArchiveOption}
                onRemoveOption={removeOption}
                onUpdateOption={updateOption}
                option={option}
                options={options}
                sortableId={fieldOptionDraggableId(option, index)}
              />
            ))}
          </div>
        </DndSortableVerticalList>
        <Button className="w-fit" disabled={disabled} onClick={addOption} type="button" variant="ghost">
          <Plus />
          {t("fieldDialog.newOption")}
        </Button>
      </div>
    </Field>
  );
}

function FieldOptionRow({
  disabled,
  fieldId,
  index,
  onArchiveOption,
  onRemoveOption,
  onUpdateOption,
  option,
  options,
  sortableId,
}: {
  disabled?: boolean;
  fieldId?: string;
  index: number;
  onArchiveOption?: (optionId: string, replacementOptionId: string | null) => Promise<void>;
  onRemoveOption: (index: number) => void;
  onUpdateOption: (index: number, patch: Partial<WorkObjectFieldOptionInput>) => void;
  option: WorkObjectFieldOptionInput;
  options: WorkObjectFieldOptionInput[];
  sortableId: string;
}) {
  const t = useT();
  const sortable = useDndSortableItem(sortableId, { disabled });
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2 rounded-sm px-1 py-1 transition-colors hover:bg-muted/30",
        sortable.isDragSource && "relative z-50 opacity-80 shadow-lg",
      )}
      ref={sortable.setNodeRef}
      style={sortable.style}
    >
      <Button
        aria-label={t("a11y.drag", { name: option.value || t("a11y.fallbackOption") })}
        className="shrink-0 cursor-grab touch-none active:cursor-grabbing"
        disabled={disabled}
        size="icon-xs"
        type="button"
        variant="ghost"
        {...sortable.dragProps}
      >
        <GripVertical />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              aria-label={t("fieldDialog.chooseOptionColor")}
              className="size-7 shrink-0 rounded-sm border border-border/70 p-0"
              disabled={disabled}
              type="button"
              variant="ghost"
            />
          }
        >
          <span className="size-3.5 rounded-sm" style={{ backgroundColor: option.color }} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="grid grid-cols-4 gap-1 p-2">
          {optionColors.map((color) => (
            <DropdownMenuItem
              aria-label={t("a11y.pickColor", { color })}
              className="size-7 justify-center p-0"
              key={color}
              onClick={() => onUpdateOption(index, { color })}
            >
              <span className="size-4 rounded-sm" style={{ backgroundColor: color }} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Input
        aria-label={t("fieldDialog.optionName")}
        className="h-8 min-w-0 flex-1 bg-transparent"
        disabled={disabled}
        onChange={(event) => onUpdateOption(index, { value: event.target.value })}
        placeholder={t("fieldDialog.option")}
        value={option.value}
      />
      {option.id && fieldId && onArchiveOption ? (
        <ArchiveOptionDialog
          disabled={disabled}
          onArchive={(replacementOptionId) => onArchiveOption(option.id!, replacementOptionId)}
          option={option}
          replacementOptions={options.filter((replacement) => replacement.id && replacement.id !== option.id)}
        />
      ) : (
        <Button
          aria-label={t("fieldDialog.removeOption")}
          className="shrink-0"
          disabled={disabled}
          onClick={() => onRemoveOption(index)}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <Trash2 />
        </Button>
      )}
    </div>
  );
}

function ArchiveOptionDialog({
  disabled,
  onArchive,
  option,
  replacementOptions,
}: {
  disabled?: boolean;
  onArchive: (replacementOptionId: string | null) => Promise<void>;
  option: WorkObjectFieldOptionInput;
  replacementOptions: WorkObjectFieldOptionInput[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [replacementOptionId, setReplacementOptionId] = useState<string>("__clear__");
  const [isArchiving, setIsArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function archiveOption() {
    setIsArchiving(true);
    setError(null);
    try {
      await onArchive(replacementOptionId === "__clear__" ? null : replacementOptionId);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("fieldDialog.couldNotArchiveOption"));
    } finally {
      setIsArchiving(false);
    }
  }

  return (
    <AlertDialog
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setReplacementOptionId("__clear__");
          setError(null);
        }
      }}
      open={open}
    >
      <AlertDialogTrigger
        render={
          <Button
            aria-label={t("fieldDialog.archiveOption")}
            className="shrink-0"
            disabled={disabled}
            size="icon-sm"
            type="button"
            variant="ghost"
          />
        }
      >
        <Trash2 />
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("fieldDialog.archiveOptionConfirm", { option: option.value || t("fieldDialog.unnamedOption") })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("fieldDialog.movedBeforeArchive")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Field>
          <FieldLabel>{t("fieldDialog.moveValuesTo")}</FieldLabel>
          <Select
            onValueChange={(value) => {
              if (value !== null) {
                setReplacementOptionId(value);
              }
            }}
            value={replacementOptionId}
          >
            <SelectTrigger aria-label={t("fieldDialog.replacementOption")} className="w-full">
              <SelectValue>
                {replacementOptionId === "__clear__"
                  ? t("fieldDialog.clearValues")
                  : replacementOptions.find((replacement) => replacement.id === replacementOptionId)?.value ??
                    t("fieldDialog.clearValues")}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">{t("fieldDialog.clearValues")}</SelectItem>
                {replacementOptions.map((replacement) => (
                  <SelectItem key={replacement.id} value={replacement.id ?? ""}>
                    <span className="inline-flex min-w-0 items-center gap-2">
                      <span className="size-3 rounded-sm" style={{ backgroundColor: replacement.color }} />
                      <span className="min-w-0 truncate">{replacement.value || "Option"}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>{t("common.cancel")}</AlertDialogCancel>
          <Button disabled={isArchiving} onClick={archiveOption} type="button" variant="destructive">
            {isArchiving ? t("fieldDialog.archiving") : t("fieldDialog.archiveOption")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function FieldHistoryPanel({
  events,
  isLoading,
  onClose,
}: {
  events: WorkObjectFieldAuditEvent[];
  isLoading: boolean;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <aside className="flex min-h-0 shrink-0 flex-col border-t border-border/70 bg-background/45 sm:w-80 sm:border-l sm:border-t-0">
      <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border/70 px-4">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{t("fieldDialog.history")}</h3>
          <p className="truncate text-xs text-muted-foreground">{t("fieldDialog.recentChanges")}</p>
        </div>
        <Button aria-label={t("fieldDialog.closeHistory")} onClick={onClose} size="icon" type="button" variant="ghost">
          <X />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <WorkObjectFieldAuditList events={events} isLoading={isLoading} />
      </div>
    </aside>
  );
}

function LocationTree({
  ancestorChecked = false,
  checkedKeys,
  depth = 0,
  nodes,
  onCheckedChange,
}: {
  ancestorChecked?: boolean;
  checkedKeys: string[];
  depth?: number;
  nodes: LocationTreeNode[];
  onCheckedChange: (key: string, checked: boolean) => void;
}) {
  const t = useT();
  if (nodes.length === 0) {
    return <p className="px-1.5 py-1 text-xs text-muted-foreground">{t("fieldDialog.noLocations")}</p>;
  }

  return (
    <div className={cn("grid gap-0.5", depth > 0 && "ml-4 border-l border-border/40 pl-2")}>
      {nodes.map((node) => {
        const checked = checkedKeys.includes(node.key);
        const inherited = !checked && ancestorChecked;
        const indeterminate = !checked && !inherited && hasSelectedDescendant(node, checkedKeys);

        return (
          <div className="grid gap-0.5" key={node.key}>
            <LocationTreeRow
              checked={checked}
              indeterminate={indeterminate}
              inherited={inherited}
              node={node}
              onCheckedChange={(nextChecked) => onCheckedChange(node.key, nextChecked)}
            />
            {node.children.length > 0 ? (
              <LocationTree
                ancestorChecked={ancestorChecked || checked}
                checkedKeys={checkedKeys}
                depth={depth + 1}
                nodes={node.children}
                onCheckedChange={onCheckedChange}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function LocationTreeRow({
  checked,
  indeterminate,
  inherited,
  node,
  onCheckedChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  inherited: boolean;
  node: LocationTreeNode;
  onCheckedChange: (checked: boolean) => void;
}) {
  const t = useT();
  const Icon = node.icon;
  const isInteractive = !inherited;

  return (
    <Field
      className={cn(
        "min-h-8 rounded-sm px-1.5 py-1 transition-colors",
        isInteractive && "hover:bg-muted/30",
        checked && "bg-muted/45",
        inherited && "text-muted-foreground",
      )}
      orientation="horizontal"
    >
      <Checkbox
        checked={checked}
        disabled={inherited}
        indeterminate={indeterminate}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <FieldContent className="min-w-0">
        <FieldLabel
          className={cn("flex w-full items-center justify-between gap-2", isInteractive && "cursor-pointer")}
          onClick={(event) => {
            event.preventDefault();
            if (isInteractive) {
              onCheckedChange(!checked);
            }
          }}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="grid size-5 shrink-0 place-items-center text-muted-foreground">
              <Icon className="size-3.5" />
            </span>
            <span className="min-w-0 truncate">{node.label}</span>
          </span>
          <span className="shrink-0 text-[0.625rem] text-muted-foreground">
            {inherited ? t("fieldDialog.inherited") : scopeTypeLabel(node.scopeType, t)}
          </span>
        </FieldLabel>
      </FieldContent>
    </Field>
  );
}



function scopeTypeLabel(scopeType: string, t: Translate) {
  const labels: Record<string, string> = {
    board: "fieldDialog.scopeBoard",
    folder: "fieldDialog.scopeFolder",
    space: "fieldDialog.scopeSpace",
    workspace: "fieldDialog.scopeWorkspace",
  };
  const key = labels[scopeType];
  return key ? t(key) : scopeType;
}

function toggleKey(keys: string[], key: string, checked: boolean) {
  if (checked) {
    return keys.includes(key) ? keys : [...keys, key];
  }

  return keys.filter((item) => item !== key);
}

function toggleLocationKey(keys: string[], tree: LocationTreeNode[], key: string, checked: boolean) {
  if (!checked) {
    return keys.filter((item) => item !== key);
  }

  const node = findLocationNode(tree, key);
  const descendantKeys = node ? new Set(getDescendantKeys(node)) : new Set<string>();
  const ancestorKeys = new Set(getAncestorKeys(tree, key));
  const withoutCoveredKeys = keys.filter((item) => item !== key && !descendantKeys.has(item) && !ancestorKeys.has(item));
  return [...withoutCoveredKeys, key];
}

function findLocationNode(nodes: LocationTreeNode[], key: string): LocationTreeNode | null {
  for (const node of nodes) {
    if (node.key === key) {
      return node;
    }
    const child = findLocationNode(node.children, key);
    if (child) {
      return child;
    }
  }
  return null;
}

function getDescendantKeys(node: LocationTreeNode): string[] {
  return node.children.flatMap((child) => [child.key, ...getDescendantKeys(child)]);
}

function getAncestorKeys(nodes: LocationTreeNode[], key: string, ancestors: string[] = []): string[] {
  for (const node of nodes) {
    if (node.key === key) {
      return ancestors;
    }
    const childAncestors = getAncestorKeys(node.children, key, [...ancestors, node.key]);
    if (childAncestors.length > 0) {
      return childAncestors;
    }
  }
  return [];
}

function hasSelectedDescendant(node: LocationTreeNode, checkedKeys: string[]): boolean {
  return node.children.some((child) => checkedKeys.includes(child.key) || hasSelectedDescendant(child, checkedKeys));
}











function rollupOperatorLabel(operator: string, t: Translate) {
  const knownOperators = ["avg", "count", "empty", "filled", "max", "min", "sum"];
  return knownOperators.includes(operator) ? t(`rollupOperator.${operator}`) : operator;
}

function fieldNameErrorText(error: FieldNameError | null, t: Translate) {
  return error === null ? null : t(error === "tooLong" ? "fieldDialog.nameTooLong" : "fieldDialog.nameTooShort");
}

function fieldKeyErrorText(error: FieldKeyError | null, t: Translate) {
  if (error === null) return null;
  if (error === "duplicate") return t("fieldDialog.keyDuplicate");
  return t(error === "tooLong" ? "fieldDialog.keyTooLong" : "fieldDialog.keyTooShort");
}




function nextOptionRank(index: number) {
  return `a${String(index + 1).padStart(4, "0")}`;
}

function fieldOptionDraggableId(option: WorkObjectFieldOptionInput, index: number) {
  return option.id ?? `new-option:${index}:${option.rank}`;
}

function rerankOptions(options: WorkObjectFieldOptionInput[]) {
  return options.map((option, index) => ({ ...option, rank: nextOptionRank(index) }));
}

// Правило записывается только когда его задали: пустое поле — это «без
// ограничения», а не ноль. Иначе сохранение формы молча запретило бы
// отрицательные значения там, где они законны.
function numberRangeInput(min: string, max: string, step: string) {
  const read = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  };
  const range = { max: read(max), min: read(min), step: read(step) };
  return range.max === undefined && range.min === undefined && range.step === undefined ? null : range;
}

function NumberRuleInput({
  disabled,
  label,
  onChange,
  value,
}: {
  disabled: boolean;
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {label}
      <Input
        className="h-8 w-24"
        disabled={disabled}
        inputMode="decimal"
        onChange={(event) => onChange(event.target.value)}
        value={value}
      />
    </label>
  );
}
